/**
 * The duel's connection lifecycle, as a table rather than a pile of booleans.
 *
 * Written as data for the same reason the realism panel is (`flight/tiers.ts`):
 * a reader can check the whole legal graph at a glance, an illegal transition
 * is a `undefined` return rather than a state nobody thought about, and the
 * test can assert every phase and every event are reachable instead of
 * asserting the four paths somebody remembered.
 *
 * `disconnected` is reachable from every non-terminal phase and is handled by
 * the table, not by a special case, so "the tab closed during the handshake"
 * behaves exactly like "the tab closed mid-fight".
 */

export const DUEL_PHASES = [
  /** Nothing open yet — the lobby's resting state. */
  "idle",
  /** Talking to the room relay, waiting for a second player. */
  "signaling",
  /** Both players present; the WebRTC offer/answer/ICE exchange is running. */
  "connecting",
  /** DataChannel open, aeroplanes being picked — no encounter yet. */
  "synced",
  /** Both peers stepping the same encounter. */
  "running",
  /** The fight was decided, or somebody quit cleanly. Rematch is legal from here. */
  "ended",
  /** The two encounters stopped agreeing. Terminal. */
  "desynced",
  /**
   * The peer is not running this build, so there is no fight the two of them
   * could agree on (`build-gate.ts`). Terminal, and deliberately *not*
   * `desynced`: a desync is a fight that went wrong, this is one that was never
   * possible, and the player is owed the difference.
   */
  "mismatched",
  /** The peer or the transport went away. Terminal. */
  "disconnected",
] as const;

export type DuelPhase = (typeof DUEL_PHASES)[number];

export const DUEL_EVENTS = [
  "join",
  "peerJoined",
  "channelOpen",
  "setupAgreed",
  "duelOver",
  "hashMismatch",
  "buildMismatch",
  "rematch",
  "peerLeft",
] as const;

export type DuelEvent = (typeof DUEL_EVENTS)[number];

type PhaseRow = Partial<Record<DuelEvent, DuelPhase>>;

/** Losing the peer is legal wherever a peer could still be expected. */
const LOST: PhaseRow = { peerLeft: "disconnected" };

/**
 * Learning the peer is a different build is legal from the moment there is a
 * channel to hear it on, and stays legal for as long as one is open — the
 * `hello` frames cross on channel-open, but a refused `setup` proves the same
 * thing at launch, and a rematch is a second launch.
 */
const WRONG_BUILD: PhaseRow = { buildMismatch: "mismatched" };

export const DUEL_TRANSITIONS: Readonly<Record<DuelPhase, PhaseRow>> = {
  idle: { join: "signaling" },
  signaling: { peerJoined: "connecting", ...LOST },
  connecting: { channelOpen: "synced", ...WRONG_BUILD, ...LOST },
  synced: { setupAgreed: "running", ...WRONG_BUILD, ...LOST },
  running: { duelOver: "ended", hashMismatch: "desynced", ...WRONG_BUILD, ...LOST },
  ended: { rematch: "synced", ...WRONG_BUILD, ...LOST },
  desynced: {},
  mismatched: {},
  disconnected: {},
};

/** The phase this event moves to, or `undefined` when it does not apply here. */
export function nextPhase(phase: DuelPhase, event: DuelEvent): DuelPhase | undefined {
  return DUEL_TRANSITIONS[phase][event];
}

/** True for a phase no event can leave. */
export function isTerminal(phase: DuelPhase): boolean {
  return Object.keys(DUEL_TRANSITIONS[phase]).length === 0;
}

/** True while the two peers are stepping the same encounter. */
export function isFlying(phase: DuelPhase): boolean {
  return phase === "running";
}

export class DuelMachine {
  private current: DuelPhase = "idle";

  get phase(): DuelPhase {
    return this.current;
  }

  /** Applies an event, returning the resulting phase. An event with no transition is ignored. */
  apply(event: DuelEvent): DuelPhase {
    this.current = nextPhase(this.current, event) ?? this.current;
    return this.current;
  }

  /** True when the event would actually move the machine. */
  accepts(event: DuelEvent): boolean {
    return nextPhase(this.current, event) !== undefined;
  }
}
